import { useEffect, useState, useCallback } from "react"
import { supabase } from "@/lib/supabase"
import { useAuth } from "@/components/auth-context"
import { subDays } from "date-fns"

export interface Notification {
    id: string
    title: string
    message: string
    type: 'info' | 'warning' | 'success' | 'alert'
    is_read: boolean
    link?: string
    created_at: string
}

export function useNotifications() {
    const { user } = useAuth()
    const [notifications, setNotifications] = useState<Notification[]>([])
    const [unreadCount, setUnreadCount] = useState(0)
    const [loading, setLoading] = useState(true)

    const fetchNotifications = useCallback(async () => {
        if (!user) return

        // Auto-purge 1: eliminar notificaciones leídas con más de 7 días
        const sevenDaysAgo = subDays(new Date(), 7).toISOString()
        await supabase
            .from("notifications")
            .delete()
            .eq("user_id", user.id)
            .eq("is_read", true)
            .lt("created_at", sevenDaysAgo)

        // Auto-purge 2: eliminar CUALQUIER notificación con más de 30 días
        // (incluye notificaciones de actualización no leídas que ya son obsoletas)
        const thirtyDaysAgo = subDays(new Date(), 30).toISOString()
        await supabase
            .from("notifications")
            .delete()
            .eq("user_id", user.id)
            .lt("created_at", thirtyDaysAgo)

        const { data, error } = await supabase
            .from("notifications")
            .select("*")
            .eq("user_id", user.id)
            .order("created_at", { ascending: false })
            .limit(20)

        if (error) {
            console.error("Error fetching notifications:", error)
        } else {
            setNotifications(data || [])
            setUnreadCount(data?.filter(n => !n.is_read).length || 0)
        }
        setLoading(false)
    }, [user])

    const markAsRead = async (id: string) => {
        // Optimistic update
        setNotifications(prev => prev.map(n =>
            n.id === id ? { ...n, is_read: true } : n
        ))
        setUnreadCount(prev => Math.max(0, prev - 1))

        await supabase
            .from("notifications")
            .update({ is_read: true })
            .eq("id", id)
    }

    const markAllAsRead = async () => {
        if (unreadCount === 0) return

        // Optimistic update
        setNotifications(prev => prev.map(n => ({ ...n, is_read: true })))
        setUnreadCount(0)

        await supabase
            .from("notifications")
            .update({ is_read: true })
            .eq("user_id", user?.id)
            .eq("is_read", false)
    }

    useEffect(() => {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        fetchNotifications()

        // Realtime subscription
        const channel = supabase
            .channel('notifications_changes')
            .on(
                'postgres_changes',
                {
                    event: 'INSERT',
                    schema: 'public',
                    table: 'notifications',
                    filter: `user_id=eq.${user?.id}`
                },
                (payload) => {
                    const newNotification = payload.new as Notification
                    setNotifications(prev => {
                        // Prevent duplicates if already in state (useful for race conditions)
                        if (prev.some(n => n.id === newNotification.id)) return prev
                        return [newNotification, ...prev]
                    })
                    setUnreadCount(prev => prev + 1)
                }
            )
            .subscribe()

        return () => {
            supabase.removeChannel(channel)
        }
    }, [user, fetchNotifications])

    const clearRead = async () => {
        // Optimistic update
        setNotifications(prev => prev.filter(n => !n.is_read))

        await supabase
            .from("notifications")
            .delete()
            .eq("user_id", user?.id)
            .eq("is_read", true)
    }

    const clearAll = async () => {
        // Optimistic update
        setNotifications([])
        setUnreadCount(0)

        await supabase
            .from("notifications")
            .delete()
            .eq("user_id", user?.id)
    }

    return {
        notifications,
        unreadCount,
        loading,
        markAsRead,
        markAllAsRead,
        clearRead,
        clearAll,
        refresh: fetchNotifications
    }
}
